import { differenceInDays, sub, type Duration } from 'date-fns';
import { interpolateStringWithData } from 'utils';

/**
 * Formats a countdown duration into a localizable string, showing only the two
 * largest whole time units that apply.
 *
 * Uses `differenceInDays` for an accurate total day count (handles months/years correctly),
 * and reads `hours`/`minutes`/`seconds` from the provided `date-fns` Duration object.
 * Picks a template by the largest unit remaining:
 * - `formats.withDays` when at least one full day remains (`{{days}}d {{hours}}h`)
 * - `formats.hoursOnly` when less than a day but at least an hour remains (`{{hours}}h {{minutes}}m`)
 * - `formats.minutesOnly` when less than an hour remains (`{{minutes}}m {{seconds}}s`, e.g. `0m 42s`)
 *
 * `formats.minutesOnly` is optional: when omitted, the sub-hour case falls back to
 * `formats.hoursOnly` (the pre-seconds-tier behavior), so callers that don't want a
 * seconds countdown can leave it off.
 *
 * `countdownDuration` must be the result of `intervalToDuration({ start: now, end: countdownDate })`
 * for some consistent `now`. `now` is inferred internally via `sub(countdownDate, countdownDuration)`,
 * so passing an inconsistent duration will produce incorrect day counts.
 *
 * Templates support `{{days}}`, `{{hours}}`, `{{minutes}}`, and `{{seconds}}` placeholders.
 *
 * @example
 * // "3d 4h"
 * formatCountdown(endsAt, duration, { withDays: '{{days}}d {{hours}}h', hoursOnly: '{{hours}}h {{minutes}}m', minutesOnly: '{{minutes}}m {{seconds}}s' });
 *
 * @example
 * // "0m 42s" (less than a minute remaining)
 * formatCountdown(endsAt, duration, { withDays: '{{days}}d {{hours}}h', hoursOnly: '{{hours}}h {{minutes}}m', minutesOnly: '{{minutes}}m {{seconds}}s' });
 */
export function formatCountdown(
  countdownDate: Date,
  countdownDuration: Duration,
  formats: { withDays: string; hoursOnly: string; minutesOnly?: string }
): string {
  const totalDays = differenceInDays(
    countdownDate,
    sub(countdownDate, countdownDuration)
  );
  const hours = countdownDuration.hours ?? 0;
  const minutes = countdownDuration.minutes ?? 0;
  const seconds = countdownDuration.seconds ?? 0;

  let template: string;
  if (totalDays > 0) {
    template = formats.withDays;
  } else if (hours > 0 || !formats.minutesOnly) {
    template = formats.hoursOnly;
  } else {
    template = formats.minutesOnly;
  }

  return interpolateStringWithData(template, {
    days: String(totalDays),
    hours: String(hours),
    minutes: String(minutes),
    seconds: String(seconds),
  });
}

export type TimeFrame =
  | 'a few seconds ago'
  | 'minute'
  | 'minutes'
  | 'hour'
  | 'hours'
  | 'day'
  | 'month'
  | 'year'
  | 'days'
  | 'months'
  | 'years';

export type CalculateTimeAgoProps = (date: Date) => {
  time: number;
  frame: TimeFrame;
};

/**
 * Calculates the time elapsed since a given date and returns a structured value and frame.
 *
 * Possible frames (in ascending order): `'a few seconds ago'`, `'minute'`/`'minutes'`,
 * `'hour'`/`'hours'`, `'day'`/`'days'`, `'month'`/`'months'`, `'year'`/`'years'`.
 *
 * Day counts are based on UTC calendar days (each date truncated to UTC midnight), not raw
 * millisecond duration, so results are DST-safe and match user expectations (e.g. "10 days ago"
 * stays 10 across a daylight-saving transition). The hours→days boundary also uses the calendar-day
 * diff so a spring-forward "yesterday" correctly returns `{ time: 1, frame: 'day' }`.
 *
 * @param date - The date from which to calculate the elapsed time.
 * @returns An object with the elapsed time and its frame (e.g., `{ time: 5, frame: 'days' }`).
 */
export const calculateTimeAgo: CalculateTimeAgoProps = (date: Date) => {
  if (isNaN(date.getTime())) {
    return { time: 0, frame: 'days' };
  }

  const now = new Date();

  const diffInMs = now.getTime() - date.getTime();

  // Truncate to UTC midnight so day count is based on calendar days, not raw ms (DST-safe)
  const toUTCMidnight = (d: Date) =>
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const diffInDays = Math.round(
    (toUTCMidnight(now) - toUTCMidnight(date)) / (1000 * 60 * 60 * 24)
  );

  if (diffInMs < 1000 * 60) {
    return { time: 0, frame: 'a few seconds ago' };
  }

  if (diffInMs < 1000 * 60 * 60) {
    const minutes = Math.floor(diffInMs / (1000 * 60));
    return { time: minutes, frame: minutes === 1 ? 'minute' : 'minutes' };
  }

  // Use calendar-day diff to force "1 day" only for the DST spring-forward edge case:
  // when elapsed ms is just under 24 h but the UTC calendar day has already turned over
  // (e.g. 23 h gap across spring-forward). A normal midnight crossing (e.g. 23:30→00:30,
  // only 1 h ago) has diffInMs well below the 23 h threshold and stays in the hours branch.
  const isDSTEdge = diffInDays >= 1 && diffInMs >= 23 * 60 * 60 * 1000;
  if (diffInMs < 1000 * 60 * 60 * 24 && !isDSTEdge) {
    const hours = Math.floor(diffInMs / (1000 * 60 * 60));
    return { time: hours, frame: hours === 1 ? 'hour' : 'hours' };
  }

  const months =
    (now.getUTCFullYear() - date.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - date.getUTCMonth());

  if (diffInDays < 30 || months < 1) {
    return { time: diffInDays, frame: diffInDays === 1 ? 'day' : 'days' };
  } else if (diffInDays < 365) {
    return { time: months, frame: months === 1 ? 'month' : 'months' };
  } else {
    let years = now.getUTCFullYear() - date.getUTCFullYear();
    if (
      now.getUTCMonth() < date.getUTCMonth() ||
      (now.getUTCMonth() === date.getUTCMonth() &&
        now.getUTCDate() < date.getUTCDate())
    ) {
      years--;
    }
    return { time: years, frame: years === 1 ? 'year' : 'years' };
  }
};

/**
 * Calculates the time elapsed since the provided date and returns it in a structured format.
 *
 * If a valid date is provided, it returns the result of `calculateTimeAgo` for that date.
 * If no date is provided, it defaults to `{ time: 0, frame: 'days' }`.
 *
 * @param date - The date from which to calculate the elapsed time.
 * @returns An object representing the elapsed time and its frame (e.g., days, hours).
 */
export const getEditedTimeAgo: CalculateTimeAgoProps = (date: Date) => {
  if (isNaN(date.getTime())) {
    return { time: 0, frame: 'days' };
  }
  return calculateTimeAgo(date);
};
